import { useCallback, useEffect, useRef, useState } from "react"

import { WarningIcon, WarningTwoIcon } from "@chakra-ui/icons"
import {
  Box,
  Button,
  Image,
  Tag,
  Text,
  TableContainer,
  Table,
  Thead,
  Th,
  Tbody,
  Td,
  useMediaQuery,
  Skeleton,
  SkeletonText,
  Tr,
  Center,
  useToast,
  Flex,
  Menu,
  MenuButton,
  MenuList,
} from "@chakra-ui/react"
import format from "date-fns/format"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import useSWR from "swr"

import { useAppDispatch, useAppSelector } from "app/hooks"
import AddWeightPopup from "components/add-weight-popup/add-weight-popup"
import CustomSearch from "components/custom-search-input/customsearch"
import CustomCheckBox from "components/customChakra/CustomCheckBox"
import CustomTableRow from "components/customChakra/CustomTableRow"
import CustomTh from "components/customChakra/CustomTh"
import PropertyStateDropdown from "components/property-state-dropdown/property-state-dropdown"
import MobileSideMenu from "components/sidemenu/mobilesidemenu"
import PageSelector from "components/table/page-selector"
import Pagination from "components/table/pagination"
import RoundedBadge from "components/table/rounded-badge"
// import TagDetailsModal from "components/tag-details-modal/tag-details-modal"
import TagButton from "components/TagButton/TagButton"
import useAlertHook from "hooks/useAlertHook/useAlertHook"
import useIsPermitted from "hooks/useIsPermittedHook/useIsPermitted"
import useToastHook from "hooks/useToastHook/useToastHook"
import { apiClient } from "services/client"
import {
  CompanyTags,
  GetLivePropertiesRequestData,
  // PmcDefaults,
  PMCIdsToDisplayUnitNumber,
} from "services/common"
import { INTERHOME_PMCID, VACASA_PMCID } from "services/constant"
import {
  getCompany,
  selectCompanyTags,
  selectCurrentPmc,
  selectIsPmcWhimstay,
} from "store/auth/authSlice"
import { selectListingsPageMenuItems } from "store/ui/uiSlice"
import { customSlugify, isOlderThen30 } from "utils/common-functions"
import { IMAGE_POSTFIX_MOBILECARD } from "utils/global-constants"
import { decodeHTMLEntities } from "utils/html-dom"
import { getTableRowCount } from "utils/local-storage"

const SkeletonTableRow = () => (
  <Tr>
    <Td />
    <Td>
      <Skeleton height='52px' width='72px' borderRadius='6px' />
    </Td>
    <Td>
      <SkeletonText noOfLines={2} w='full' />
    </Td>
    <Td>
      <SkeletonText noOfLines={2} w='full' />
    </Td>
    <Td>
      <SkeletonText noOfLines={2} w='full' />
    </Td>
    <Td>
      <SkeletonText noOfLines={2} w='full' />
    </Td>
    <Td>
      <SkeletonText noOfLines={2} w='full' />
    </Td>
  </Tr>
)

const fetcher = async ({
  url,
  companyTagData,
  ...params
}: {
  url: string
  companyTagData: CompanyTags
} & GetLivePropertiesRequestData) => {
  const data = await apiClient.getLiveProperties({ params })

  return data.data.data
}

const PUBLIC_TAGS = new Set([
  "No Availability",
  "No Calendar Data",
  "Missing MinLos",
  "Missing Description",
  "Missing Bathroom Data",
  "Missing Bedroom Data",
  "Missing Images",
  "Missing LatLong",
  "Missing Cancellation Policy Company Level",
  "Missing Cancellation Policy Property Level",
  "Missing Property Damage Protection Company Level",
  "Missing Property Damage Protection Property Level",
  "Discount Template Not Applied",
  "Contact Template Not Applied",
  "Address/Map Pin Mismatch",
])

const IncompleteProperties = () => {
  const displayToast = useToastHook()
  const hasAccessTo = useIsPermitted()
  const [alert, showAlert, closeAlert] = useAlertHook()
  // const [isLargerThan768] = useMediaQuery("(min-width: 768px)") || [true]
  const [isMobile] = useMediaQuery("(max-width: 1024px)")

  const [searchParams, setSearchParams] = useSearchParams()
  const [term, setTerm] = useState<string>(searchParams.get("keyword") || "")
  const [pageSize, setPageSize] = useState<number>(+getTableRowCount() || 5)
  const [pageStartIndex, setPageStartIndex] = useState<number>(
    parseInt(searchParams.get("pageNo") || "0", 10),
  )
  const [totalItem, setTotalItem] = useState(0)

  const [sortBy, setSortBy] = useState<string>("")
  const [sortDirection, setSortDirection] = useState<string>("")

  // for state list
  const [stateList, setStateList] = useState<string[]>([])
  const [selectedState, setSelectedState] = useState<string>("ALL")

  const currentPmc = useAppSelector(selectCurrentPmc)
  const isPmcTypeWhimstay = useAppSelector(selectIsPmcWhimstay)
  const listingPageMenuItems = useAppSelector(selectListingsPageMenuItems)
  const companyTagData = useAppSelector(selectCompanyTags)

  const dispatch = useAppDispatch()

  useEffect(() => {
    // Set a query parameter
    setSearchParams({
      keyword: term,
      noOfRecords: `${pageSize}`, // Convert number to string using template literal
      pageNo: `${pageStartIndex}`, // Convert number to string using template literal
    })
  }, [setSearchParams, term, pageSize, pageStartIndex])

  const {
    data: swrdata,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    error,
    mutate,
  } = useSWR(
    currentPmc?.pmc_id
      ? {
          url: `${process.env.REACT_APP_BASE_API_URL || ""}/properties/all`,
          pmcId: currentPmc?.pmc_id,
          active: true,
          isHide: false,
          isInactive: false,
          isDelete: false,
          noOfRecords: pageSize,
          pageNo: pageStartIndex,
          keyword: term,
          sortBy,
          sortDirection,
          state: stateList?.length > 1 ? selectedState : "",
          companyTagData,
          isIncomplete: true,
        }
      : null,
    fetcher,
  )

  // for next page prefetching
  useSWR(
    currentPmc?.pmc_id
      ? {
          url: `${process.env.REACT_APP_BASE_API_URL || ""}/properties/all`,
          pmcId: currentPmc?.pmc_id,
          active: true,
          isHide: false,
          isInactive: false,
          isDelete: false,
          noOfRecords: pageSize,
          pageNo: pageStartIndex + 1,
          keyword: term,
          sortBy,
          sortDirection,
          state: stateList?.length > 1 ? selectedState : "",
          //   isIncomplete: true,
        }
      : null,
    fetcher,
  )

  const isLoading = !swrdata && !error

  const navigate = useNavigate()

  const [checkedItems, setCheckedItems] = useState<string[]>([])
  const [checkedProperties, setCheckedProperties] = useState<string[]>([])
  // const [defaults, setDefaults] = useState<PmcDefaults | null>(null)

  const noOfSelectedItems = checkedItems.length
  const allChecked =
    checkedItems.length === swrdata?.content.length &&
    swrdata?.content.length !== 0

  // const getPmcDetailsIsComplete = useCallback(() => {
  //   if (currentPmc?.pmc_id) {
  //     apiClient
  //       .pmcDefaultCheck({
  //         params: {
  //           pmcId: currentPmc?.pmc_id,
  //         },
  //       })
  //       .then((response) => {
  //         setDefaults(response.data.data)
  //         return true
  //       })
  //       .catch(() => {})
  //   }
  // }, [currentPmc?.pmc_id])

  // On change of pmc check if user already selected properties then reset those properties
  useEffect(() => {
    // getPmcDetailsIsComplete()
    setCheckedItems([])
    setCheckedProperties([])
    setTerm("")
    setPageStartIndex(0)
  }, [currentPmc?.pmc_id])

  // if fetched data change based on new search or page change then checked items should be reset other wise options will show enabled even if we not have those data
  useEffect(() => {
    setCheckedItems([])
    setCheckedProperties([])
  }, [term])

  // on page size change we reset checked items
  useEffect(() => {
    setCheckedItems([])
    setCheckedProperties([])
    setPageStartIndex(pageStartIndex)
  }, [pageSize, pageStartIndex])

  // on page change we reset checked items
  useEffect(() => {
    setCheckedItems([])
    setCheckedProperties([])
  }, [pageStartIndex])

  useEffect(() => {
    // If this is the first call, store the initial totalPages value
    if (pageStartIndex === 0) {
      setTotalItem(swrdata?.totalElements || 0)
    }
  }, [pageStartIndex, swrdata?.totalElements])

  const handleSort = useCallback(
    (key: string) => {
      // on sort change we reset checked items
      setCheckedItems([])
      setCheckedProperties([])
      if (sortBy === key) {
        // just change direction
        setSortDirection((x) => (x === "ASC" ? "DESC" : "ASC"))
      } else {
        // just change key
        setSortBy(key)
      }
    },
    [setSortBy, sortBy],
  )

  const deleteProperties = useCallback(
    (ids: string[]) => {
      if (currentPmc?.pmc_id) {
        apiClient
          .deleteProperties({
            data: {
              propertyFor: "PUBLISH",
              allDelete: false,
              pmcId: currentPmc?.pmc_id,
              pmsUnitIds: ids,
            },
          })
          .then(async () => {
            displayToast({
              message: "Your property have been deleted",
            })
            setCheckedItems([])
            setCheckedProperties([])
            // if items deleted then that selected page could be empty so reset page to first page
            setPageStartIndex(0)
            if (swrdata?.content && swrdata?.content?.length > 0) {
              // update the local data immediately and revalidate (refetch)
              await mutate({
                ...swrdata,
                content: swrdata?.content.filter(
                  (item) => !ids.includes(item.pmsUnitId),
                ),
              })
            }
            return true
          })
          .catch(() => {
            displayToast({
              message: "Something went wrong! Can not delete the property.",
            })
          })
      }
    },
    [currentPmc?.pmc_id, displayToast, swrdata, mutate],
  )

  const toast = useToast()
  const toastIdRef = useRef<number | string>("")

  const publishProperties = useCallback(
    (ids: string[]) => {
      if (currentPmc?.pmc_id) {
        toastIdRef.current = toast({
          description: "Publishing Properties",
          status: "loading",
          position: "top-right",
          duration: 60 * 3 * 1000,
        })

        if (currentPmc?.status === "IN-PROCESS") {
          apiClient
            .companyStatus({
              params: {
                pmcId: currentPmc?.pmc_id,
              },
            })
            .then(() => true)
            .catch(() => true)
        }
        apiClient
          .publishProperties({
            data: {
              eventType: "PUBLISH",
              allUpdate: false,
              pmcId: currentPmc?.pmc_id,
              pmsUnitIds: ids,
            },
          })
          .then(async () => {
            // toastIdRef.current = toast({
            //   description: "Publishing Properties: 0%",
            //   status: "loading",
            //   position: "top-right",
            //   duration: 60 * 3 * 1000,
            // })

            // const callItForPolling = () => {
            //   let runs = 60 * 3
            //   const i = setInterval(async () => {
            //     // eslint-disable-next-line promise/no-nesting
            //     const res = await apiClient.checkPublishPropertiesStatus({
            //       data: { eventType: "PUBLISH", pmcId: currentPmc?.pmc_id },
            //     })

            //     const isFinished =
            //       res.data.message === "Process has been completed."
            //     let percentageDone = "0"
            //     if (isFinished) {
            //       percentageDone = "100"
            //     } else if (res.data.data.totalPmsUnitIdsCount === 0) {
            //       percentageDone = "0"
            //     } else {
            //       percentageDone = (
            //         (100 * res.data.data.processedPmsUnitIdsCount) /
            //         res.data.data.totalPmsUnitIdsCount
            //       ).toFixed(0)
            //     }

            //     if (toastIdRef.current) {
            //       toast.update(toastIdRef.current, {
            //         description: `Publishing Properties: ${percentageDone}%`,
            //         status: isFinished ? "success" : "loading",
            //         position: "top-right",
            //         duration: isFinished ? 3000 : 60 * 3 * 1000,
            //       })
            //     }

            //     if (isFinished) {
            //       setCheckedItems([])
            //       setCheckedProperties([])
            //       // if items live then that selected page could be empty so reset page to first page
            //       setPageStartIndex(0)
            //       clearInterval(i)
            //       // eslint-disable-next-line promise/no-nesting
            //       mutate()
            //         .then(() => true)
            //         .catch(() => true)
            //       return
            //     }

            //     runs -= 1
            //     if (runs === 0) {
            //       clearInterval(i)
            //       // eslint-disable-next-line promise/no-nesting
            //       mutate()
            //         .then(() => true)
            //         .catch(() => true)
            //     }
            //   }, 1000)
            // }
            // callItForPolling()
            setCheckedItems([])
            setCheckedProperties([])
            // if items live then that selected page could be empty so reset page to first page
            setPageStartIndex(0)
            // eslint-disable-next-line promise/no-nesting
            mutate()
              .then(() => true)
              .catch(() => true)
            if (toastIdRef.current) {
              toast.update(toastIdRef.current, {
                description: `Published the Properties`,
                status: "success",
                position: "top-right",
                duration: 3000,
              })
            }
            await dispatch(
              getCompany({
                params: {
                  pmcId: currentPmc?.pmc_id,
                },
              }),
            ).unwrap()

            return true
          })
          .catch((err) => {
            if (toastIdRef.current) {
              toast.update(toastIdRef.current, {
                description: `Faild to publish the Properties`,
                status: "error",
                position: "top-right",
                duration: 3000,
              })
            }
            displayToast({
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              message:
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                err?.errorMessage ||
                "Something went wrong! Can not publish the property.",
            })
          })
      }
    },

    [
      currentPmc?.pmc_id,
      currentPmc?.status,
      toast,
      mutate,
      dispatch,
      displayToast,
    ],
  )

  const syncPropertues = useCallback(
    (ids: string[]) => {
      if (currentPmc?.pmc_id) {
        toastIdRef.current = toast({
          description: "Syncing Property",
          status: "loading",
          position: "top-right",
          duration: 60 * 3 * 1000,
        })
        apiClient
          .syncProperties({
            params: {
              pmcId: currentPmc?.pmc_id,
              propertyIds: ids,
            },
          })
          .then(() => {
            setCheckedItems([])
            setCheckedProperties([])
            if (toastIdRef.current) {
              toast.update(toastIdRef.current, {
                description: `Synced the Property`,
                status: "success",
                position: "top-right",
                duration: 3000,
              })
            }
            displayToast({
              message: `Property synced successfuly.`,
            })
            return true
          })
          .catch(() => {
            if (toastIdRef.current) {
              toast.update(toastIdRef.current, {
                description: `Faild to sync the Property`,
                status: "error",
                position: "top-right",
                duration: 3000,
              })
            }

            displayToast({
              message: "Something went wrong! Can not unpublish the property.",
            })
          })
      }
    },
    [currentPmc?.pmc_id, displayToast, toast],
  )

  const inactiveProperties = useCallback(
    (ids: string[]) => {
      if (currentPmc?.pmc_id) {
        toastIdRef.current = toast({
          description: "Inactivated Properties",
          status: "loading",
          position: "top-right",
          duration: 60 * 3 * 1000,
        })

        apiClient
          .inactiveProperties({
            data: {
              isHide: true,
              pmcId: currentPmc?.pmc_id,
              pmsUnitIds: ids,
            },
          })
          .then(() => {
            setCheckedItems([])
            setCheckedProperties([])
            // if items unpublished then that selected page could be empty so reset page to first page
            setPageStartIndex(0)
            // eslint-disable-next-line promise/no-nesting
            mutate()
              .then(() => true)
              .catch(() => true)
            if (toastIdRef.current) {
              toast.update(toastIdRef.current, {
                description: `Inactivated the Properties`,
                status: "success",
                position: "top-right",
                duration: 3000,
              })
            }

            return true
          })
          .catch(() => {
            if (toastIdRef.current) {
              toast.update(toastIdRef.current, {
                description: `Faild to inactive the Properties`,
                status: "error",
                position: "top-right",
                duration: 3000,
              })
            }
            displayToast({
              message: "Something went wrong! Can not inactive the property.",
            })
          })
      }
    },
    [currentPmc?.pmc_id, toast, mutate, displayToast],
  )

  // openListing
  const openListing = useCallback(
    (pmsUnitId: string) => {
      const data = swrdata?.content.find((item) => item.pmsUnitId === pmsUnitId)
      if (data)
        window.open(
          `${
            process.env.REACT_APP_WHIMSTAY_WEB_URL || ""
          }/detail/${customSlugify(
            data?.propertyName || data.urlPropertyName,
          )}/${data.propertyId}${
            hasAccessTo?.propertyDetailWithToken
              ? "?&dataToken=E76xOs7B2s0"
              : ""
          }`,
          "_blank",
        )
    },
    [hasAccessTo?.propertyDetailWithToken, swrdata?.content],
  )

  const openPreviewListing = useCallback(
    (id: string) => {
      navigate(`/listings/properties/details/${id}#propertyDetails`)
    },
    [navigate],
  )
  const openCalendar = useCallback(
    (id: string) => {
      navigate(`/listings/properties/calendar/${id}`)
    },
    [navigate],
  )

  const isSelectedItemIsInprogress =
    noOfSelectedItems === 1 &&
    swrdata?.content
      ?.find((x) => x.pmsUnitId === checkedItems[0])
      ?.propertyStatus?.toLowerCase() === "in progress"
  // const isTemplatedApplied = (
  //   defaultValue: boolean,
  //   propertyValue: boolean,
  // ) => {
  //   if (defaultValue) return defaultValue
  //   if (propertyValue) return propertyValue
  //   return false
  // }

  // for table top to scroll
  const tableLivePropRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (tableLivePropRef.current) {
      tableLivePropRef.current.scrollTop = 0
    }
  }, [pageStartIndex])

  const resetToDefault = () => {
    setCheckedItems([])
    setCheckedProperties([])
    // if items unpublished then that selected page could be empty so reset page to first page
    setPageStartIndex(0)
    // eslint-disable-next-line promise/no-nesting
    mutate()
      .then(() => true)
      .catch(() => true)
  }

  return (
    <Box display='flex' flexDir='column' gap='16px' mb={8}>
      <Box
        mb={{ base: "0px", lg2: "12px" }}
        display='flex'
        alignItems={{ base: "stretch", lg: "center" }}
        justifyContent={{ base: "flex-start", lg2: "space-between" }}
        flexDir={{ base: "column", lg: "row" }}
        flexFlow='wrap'
        gap='16px'
      >
        <Box
          display='flex'
          alignItems='center'
          justifyContent='space-between'
          gap='16px'
          flexGrow={1}
        >
          {/* title and create button */}
          <Text
            size={{
              base: "lg2",
              md: "xl",
            }}
            fontWeight={{
              base: 600,
              md: 700,
            }}
            variant='black'
          >
            Incomplete Properties
          </Text>
          {isMobile && <MobileSideMenu sidebarMenuArr={listingPageMenuItems} />}
        </Box>
        <Box
          display='flex'
          alignItems='center'
          justifyContent='flex-end'
          gap='16px'
          flexFlow='wrap'
          w={{ base: "100%", sm: "auto" }}
        >
          {isPmcTypeWhimstay && (
            <Button
              as={Link}
              to={hasAccessTo.createNewListing ? "/become-a-host/intro" : ""}
              ml={{ base: "0", sm: "auto" }}
              variant='secondary'
              size='sm'
              maxW={{ base: "full", sm: "320px" }}
              w={{ base: "full", sm: "auto" }}
              disabled={!hasAccessTo.createNewListing}
            >
              Create a new listing
            </Button>
          )}
          <Box display='flex' alignItems='center' gap='16px'>
            {/* show state list dropdown */}
            <PropertyStateDropdown
              stateList={stateList}
              setStateList={setStateList}
              selectedState={selectedState}
              setSelectedState={setSelectedState}
            />
            {/* show add weight popup */}
            {hasAccessTo?.isAdmin && (
              <AddWeightPopup
                noOfSelectedItems={noOfSelectedItems}
                checkedProperties={checkedProperties}
                mutate={mutate}
              />
            )}
          </Box>
        </Box>
      </Box>

      <Box
        display='flex'
        alignItems='center'
        justifyContent='space-between'
        gap='16px'
        flexWrap='wrap'
      >
        {/* search and actions buttons */}
        <Box flexGrow={1} maxW={{ base: "full", sm: "320px" }}>
          <CustomSearch
            width='full'
            term={term}
            setTerm={(e: string) => {
              setTerm(e)
              setPageStartIndex(0)
            }}
            onClear={() => setTerm("")}
          />
        </Box>
        <Box
          ml='auto'
          display='flex'
          alignItems='center'
          justifyContent='flex-end'
          flexWrap='wrap'
          gap={{ base: 2.5, lg: 8 }}
        >
          {hasAccessTo?.isAdmin && currentPmc && currentPmc.pmc_id && (
            <TagButton
              resetToDefault={resetToDefault}
              pmcId={currentPmc?.pmc_id}
              isDisabled={
                noOfSelectedItems === 0 ||
                isSelectedItemIsInprogress ||
                !hasAccessTo.publishUnpublishedProperties
              }
              checkedProperties={checkedProperties}
            >
              Tags
            </TagButton>
          )}
          <Button
            variant='secondary'
            size='sm'
            isDisabled={
              !(noOfSelectedItems === 1) || !hasAccessTo.listingLiveProperties
            }
            onClick={() => openListing(checkedItems[0])}
          >
            Listing Preview
          </Button>
          {isPmcTypeWhimstay && (
            <Button
              variant='secondary'
              size='sm'
              isDisabled={
                !(noOfSelectedItems === 1) ||
                !hasAccessTo.viewCalendarLiveProperties
              }
              onClick={() => {
                if (checkedItems.length === 1) {
                  openCalendar(checkedItems[0])
                }
              }}
            >
              Calendar
            </Button>
          )}

          {/* {!isPmcTypeWhimstay && ( */}
          {hasAccessTo?.isAdmin && (
            <Button
              variant='secondary'
              size='sm'
              isDisabled={
                !(noOfSelectedItems === 1) || !hasAccessTo.previewLiveProperties
              }
              onClick={() => {
                if (checkedItems.length === 1) {
                  openPreviewListing(checkedItems[0])
                }
              }}
            >
              Edit Listing
            </Button>
          )}
          {isPmcTypeWhimstay && (
            <Button
              variant='secondary'
              size='sm'
              // TODO add the count of property
              onClick={() => {
                showAlert({
                  message: `Are you sure you want to delete ${
                    checkedItems.length > 1
                      ? "these properties"
                      : "this property"
                  }?`,
                  pbText: "Delete",
                  sbText: "Cancel",
                  pbOnClick: () => () => {
                    deleteProperties(checkedProperties)
                    closeAlert()
                  },
                  sbOnClick: () => () => closeAlert(),
                })
              }}
              isDisabled={
                noOfSelectedItems === 0 || !hasAccessTo.deleteLiveProperties
              }
            >
              Delete
            </Button>
          )}
          {hasAccessTo?.isAdmin && (
            <>
              <Button
                variant='secondary'
                size='sm'
                onClick={() => {
                  syncPropertues(checkedProperties)
                }}
                isDisabled={
                  noOfSelectedItems === 0 ||
                  !hasAccessTo.syncLiveProperties ||
                  noOfSelectedItems > 1
                }
              >
                Sync Property
              </Button>
              <Button
                variant='secondary'
                size='sm'
                onClick={() => {
                  showAlert({
                    message: `Are you sure you want to move ${
                      checkedItems.length > 1
                        ? "these properties"
                        : "this property"
                    } to inactive?`,
                    pbText: "inactive",
                    sbText: "Cancel",
                    pbOnClick: () => () => {
                      inactiveProperties(checkedProperties)
                      closeAlert()
                    },
                    sbOnClick: () => () => closeAlert(),
                  })
                }}
                isDisabled={
                  noOfSelectedItems === 0 ||
                  !hasAccessTo.publishUnpublishedProperties
                }
              >
                Inactive
              </Button>
            </>
          )}
          <Button
            variant='secondary'
            size='sm'
            // TODO add the count of property
            onClick={() => {
              showAlert({
                message: `Are you sure you want to move ${
                  checkedItems.length > 1 ? "these properties" : "this property"
                } to live?`,
                pbText: "Live",
                sbText: "Cancel",
                pbOnClick: () => () => {
                  publishProperties(checkedProperties)
                  closeAlert()
                },
                sbOnClick: () => () => closeAlert(),
              })
            }}
            isDisabled={
              noOfSelectedItems === 0 ||
              isSelectedItemIsInprogress ||
              !hasAccessTo.publishUnpublishedProperties
            }
          >
            Live
          </Button>
        </Box>
      </Box>
      {error && (
        <Center p={6} w='full' flexDir='column'>
          <WarningTwoIcon color='primaryColor' w={16} h={16} />
          <Text size='lg' mt={8} mb={4}>
            Something went wrong.
          </Text>
        </Center>
      )}
      {!isLoading && swrdata?.content?.length === 0 && (
        <Center py={10} px={6} w='full' flexDir='column'>
          <WarningIcon color='primaryColor' w={16} h={16} />
          <Text size='lg' mt={8} mb={4}>
            No Properties listed yet.
          </Text>
        </Center>
      )}
      {!error && swrdata?.content?.length !== 0 && (
        <Box
          w={{
            base: "100vw",
            lg2: "calc(100vw - 299px)",
            xl: "calc(100vw - 391px)",
          }}
          maxWidth='full'
          overflow='auto'
          position='relative'
        >
          {/* table  */}
          <TableContainer
            border='1px solid #e1e1e1'
            borderRadius='30px'
            overflowY='auto'
            maxHeight={{
              base: "36vh",
              sm: "40vh",
              md: "50vh",
              lg2: "45vh",
              xxl: "50vh",
            }}
            ref={tableLivePropRef}
          >
            <Table>
              <Thead>
                <CustomTableRow>
                  <Th>
                    <CustomCheckBox
                      isChecked={allChecked}
                      onChange={(e) => {
                        if (e.target.checked) {
                          // Only pick items that are NOT disabled
                          const selectableItems =
                            swrdata?.content
                              .filter(
                                (item) =>
                                  !document
                                    .getElementById(`chk-${item.pmsUnitId}`)
                                    ?.hasAttribute("disabled"),
                              )
                              .map((x) => x.pmsUnitId) || []

                          const selectableProperties =
                            swrdata?.content
                              .filter(
                                (item) =>
                                  !document
                                    .getElementById(`chk-${item.pmsUnitId}`)
                                    ?.hasAttribute("disabled"),
                              )
                              .map((x) => x.propertyId) || []

                          setCheckedItems(selectableItems)
                          setCheckedProperties(selectableProperties)
                        } else {
                          setCheckedItems([])
                          setCheckedProperties([])
                        }
                      }}
                    />
                  </Th>
                  <Th />
                  <CustomTh
                    minW='230px'
                    isSortable
                    onClick={() => handleSort("propertyName")}
                  >
                    Name
                  </CustomTh>
                  {/* <Th>Status</Th> */}
                  {hasAccessTo?.isAdmin && (
                    <CustomTh
                      isSortable
                      onClick={() => handleSort("urlPropertyName")}
                    >
                      Whimstay Property Name
                    </CustomTh>
                  )}
                  <CustomTh minW='230px'>Location</CustomTh>
                  <CustomTh isSortable onClick={() => handleSort("pmsUnitId")}>
                    PMS Property ID
                  </CustomTh>
                  {currentPmc && currentPmc.pmc_id === VACASA_PMCID && (
                    <Th>Bookingpal Id</Th>
                  )}
                  {currentPmc && currentPmc.pmc_id === INTERHOME_PMCID && (
                    <Th>Interhome Id</Th>
                  )}
                  {currentPmc &&
                    PMCIdsToDisplayUnitNumber.includes(currentPmc?.pmc_id) && (
                      <Th minW='230px'>
                        <Box display='flex' alignItems='baseline' gap={1}>
                          <Text
                            as='span'
                            maxW='175px'
                            fontSize={{ base: 14, md: 18 }}
                            fontWeight={500}
                            whiteSpace='nowrap'
                            overflow='hidden'
                            textOverflow='ellipsis'
                          >
                            {currentPmc?.name}
                          </Text>
                          Id
                        </Box>
                      </Th>
                    )}
                  <CustomTh isSortable onClick={() => handleSort("updatedDt")}>
                    Last Modified
                  </CustomTh>
                  {hasAccessTo?.isAdmin && (
                    <CustomTh isSortable onClick={() => handleSort("weight")}>
                      Weight
                    </CustomTh>
                  )}
                  {/* <Th>Tag Details</Th> */}
                </CustomTableRow>
              </Thead>
              <Tbody>
                {!isLoading &&
                  swrdata?.content.map((data) => {
                    let snooze = false

                    const allTags: string[] = []

                    // Discount Missing
                    // if (!data.discountTemplateApplied)
                    //   allTags.push("Discount Missing")

                    // Custom Tags
                    if (data.propertyCustomTags?.tempHold)
                      allTags.push("Temp Hold")
                    if (data.propertyCustomTags?.tripOnly)
                      allTags.push("Trip Only")
                    if (data.propertyCustomTags?.investigatingPricing)
                      allTags.push("Investigating Pricing")
                    if (data.propertyCustomTags?.knownHighPricing)
                      allTags.push("Known High Pricing")
                    if (data.propertyCustomTags?.awaitingPmc)
                      allTags.push("Awaiting PMC")
                    if (data.propertyCustomTags?.awaitingPms)
                      allTags.push("Awaiting PMS")
                    if (data.propertyCustomTags?.dupe) allTags.push("Dupe")
                    if (snooze) allTags.push("Snooze")
                    if (data.propertyCustomTags?.inDataValidation)
                      allTags.push("In Data Validation")
                    if (data.propertyCustomTags?.engineeringFixingDataIssues)
                      allTags.push("Engineering Fixing Data Issue")
                    if (data.propertyCustomTags?.doNotModify)
                      allTags.push("Do Not Modify")
                    if (data.propertyCustomTags?.excludeGVRIncludeWS)
                      allTags.push("ExcludeGVRIncludeWS")
                    if (data.quarantine) allTags.push("Quarantine")
                    if (data.propertyCustomTags?.sharedSpace)
                      allTags.push("Shared Space")
                    if (data.propertyCustomTags?.pendingDnm)
                      allTags.push("Pending DNM")
                    if (data.propertyCustomTags?.suppressBadge)
                      allTags.push("No Badge")
                    if (data.propertyCustomTags?.taExclude)
                      allTags.push("TA Exclude")

                    // Publishing Tags
                    if (data.publishingTags?.missing_images)
                      allTags.push("Missing Images")
                    if (data.publishingTags?.missing_minLos)
                      allTags.push("Missing MinLos")
                    if (
                      data.publishingTags
                        ?.missing_cancellation_policy_company_lvl
                    )
                      allTags.push("Missing Cancellation Policy Company Level")
                    if (
                      data.publishingTags
                        ?.missing_cancellation_policy_property_lvl
                    )
                      allTags.push("Missing Cancellation Policy Property Level")
                    if (data.publishingTags?.missing_contact_template)
                      allTags.push("Contact Template Not Applied")
                    if (data.publishingTags?.missing_bathroom_data)
                      allTags.push("Missing Bathroom Data")
                    if (data.publishingTags?.missing_bedroom_data)
                      allTags.push("Missing Bedroom Data")
                    if (data.publishingTags?.missing_pdp_company_lvl)
                      allTags.push(
                        "Missing Property Damage Protection Company Level",
                      )
                    if (data.publishingTags?.missing_pdp_property_lvl)
                      allTags.push(
                        "Missing Property Damage Protection Property Level",
                      )
                    if (data.publishingTags?.missing_discount)
                      allTags.push("Discount Template Not Applied")
                    if (data.publishingTags?.missing_latlong)
                      allTags.push("Missing LatLong")
                    if (data.publishingTags?.description_less_50)
                      allTags.push("Missing Description")
                    if (data.publishingTags?.minlos_40_plus)
                      allTags.push("MinLos 40+")
                    if (data.publishingTags?.missing_calendar_data)
                      allTags.push("No Calendar Data")
                    if (data.publishingTags?.missing_availability)
                      allTags.push("No Availability")
                    if (data.publishingTags?.potential_dupe)
                      allTags.push("Potential Dupe")
                    if (data.publishingTags?.pending_price_analysis)
                      allTags.push("Pending Price Analysis")
                    if (data.publishingTags?.address_map_pin_mismatched)
                      allTags.push("Address/Map Pin Mismatch")
                    if (data.publishingTags?.taxes_zero)
                      allTags.push("Taxes Zero")

                    const finalTags = hasAccessTo?.isAdmin
                      ? allTags // admin sees everything
                      : allTags.filter((tag) => PUBLIC_TAGS.has(tag)) // others see only public tags
                    const visibleTags = finalTags.slice(0, 2)
                    const remainingTags = finalTags.slice(2)
                    const remainingCount = remainingTags.length

                    // let blockedForPublishing = allTags.some((tag) =>
                    //   blockingTags.includes(tag),
                    // )

                    // Special condition for "Incomplete"
                    // if (
                    //   (!isTemplatedApplied(
                    //     !!defaults?.isDefaultContactTemplate,
                    //     !!data.contactTemplateApplied,
                    //   ) &&
                    //     allTags.includes("Incomplete")) ||
                    //   (!data.discountTemplateApplied &&
                    //     allTags.includes("Incomplete"))
                    // ) {
                    //   blockedForPublishing = true
                    // }

                    if (
                      data.propertyCustomTags &&
                      data.propertyCustomTags?.snooze?.startDate &&
                      data.propertyCustomTags?.snooze?.endDate
                    ) {
                      const { startDate } = data.propertyCustomTags.snooze

                      const { endDate } = data.propertyCustomTags.snooze

                      if (
                        Number(startDate) < Date.now() &&
                        Number(endDate) > Date.now()
                      ) {
                        snooze = true
                      } else {
                        snooze = false
                      }
                    }
                    const propertyName = decodeHTMLEntities(
                      data?.propertyName?.trim(),
                    )
                    const urlPropertyName = decodeHTMLEntities(
                      data?.urlPropertyName,
                    )
                    return (
                      <CustomTableRow
                        key={data.pmsUnitId}
                        selectable
                        isActive={checkedItems.includes(data.pmsUnitId)}
                      >
                        <Td>
                          <CustomCheckBox
                            id={`chk-${data.pmsUnitId}`}
                            // isDisabled={blockedForPublishing}
                            isChecked={checkedItems.includes(data.pmsUnitId)}
                            onChange={(e) => {
                              e.preventDefault()
                              e.stopPropagation()

                              if (checkedItems.includes(data.pmsUnitId)) {
                                // means remove
                                setCheckedItems((ids) =>
                                  ids.filter((x) => x !== data.pmsUnitId),
                                )
                                setCheckedProperties((ids) =>
                                  ids.filter((x) => x !== data.propertyId),
                                )
                              } else {
                                // add
                                setCheckedItems((ids) => [
                                  ...ids,
                                  data.pmsUnitId,
                                ])
                                setCheckedProperties((ids) => [
                                  ...ids,
                                  data.propertyId,
                                ])
                              }
                            }}
                          />
                        </Td>
                        <Td>
                          <Image
                            alt='property-cover'
                            src={
                              data?.imageLink?.includes("wemaste.com") ||
                              data?.imageLink?.includes("whimstay.com")
                                ? `${data.imageLink}${IMAGE_POSTFIX_MOBILECARD}`
                                : data.imageLink
                            }
                            fallbackSrc={`${
                              process.env.REACT_APP_BASENAME || ""
                            }/images/placeholder.jpg`}
                            bg='gray.100'
                            height='52px'
                            width='72px'
                            minW='72px'
                            objectFit='cover'
                            borderRadius='6px'
                          />
                        </Td>
                        <Td>
                          <Box
                            mb={1}
                            display='flex'
                            gap={2.5}
                            alignItems='center'
                            flexWrap='wrap'
                          >
                            <Text
                              fontWeight='semibold'
                              width={{ xl: "250px" }}
                              onClick={() => {
                                openListing(data.pmsUnitId)
                              }}
                              noOfLines={1}
                            >
                              {propertyName || urlPropertyName}
                            </Text>
                            <Box display='flex' alignItems='center'>
                              {!isOlderThen30(data.createdAt) && (
                                <Tag
                                  bg='#0d6f8177'
                                  mr='5px'
                                  h='24px'
                                  rounded='full'
                                >
                                  <Text
                                    variant='white'
                                    size='xs'
                                    fontWeight={400}
                                    whiteSpace='nowrap'
                                  >
                                    New Property ({" "}
                                    {isOlderThen30(data.createdAt)
                                      ? ""
                                      : format(data.createdAt, "MMM dd")}{" "}
                                    ){/* <Text fontSize='sm' >asdasd</Text> */}
                                  </Text>
                                </Tag>
                              )}
                              {/* {defaults &&
                                  (!isTemplatedApplied(
                                    defaults.isDefaultContactTemplate,
                                    data.contactTemplateApplied,
                                  ) ||
                                    !isTemplatedApplied(
                                      defaults.isDefaultPaymentAccount,
                                      data.accountApplied,
                                    )) && (
                                    <Box
                                      display='flex'
                                      alignItems='center'
                                      gap='4px'
                                    >
                                      <RoundedBadge
                                        marginRight='0px'
                                        text='Incomplete'
                                      />
                                      <TagDetailsModal
                                        isIconBtn
                                        details={{
                                          "Property Status":
                                            data.propertyStatus,
                                          "Contact Template Applied":
                                            isTemplatedApplied(
                                              defaults.isDefaultContactTemplate,
                                              data.contactTemplateApplied,
                                            )
                                              ? "Yes"
                                              : "No",
                                          "Discount Template Applied":
                                            data.discountTemplateApplied
                                              ? "Yes"
                                              : "No",
                                          "Bank Account Applied":
                                            isTemplatedApplied(
                                              defaults.isDefaultPaymentAccount,
                                              data.accountApplied,
                                            )
                                              ? "Yes"
                                              : "No",
                                        }}
                                        otherTag={
                                          !(
                                            data.accountApplied &&
                                            data.contactTemplateApplied
                                          )
                                            ? "Incomplete"
                                            : undefined
                                        }
                                        createdAt={
                                          isOlderThen30(data.createdAt)
                                            ? undefined
                                            : data.createdAt
                                        }
                                      />
                                    </Box>
                                  )} */}

                              <Box>
                                <Flex wrap='wrap' gap='10px' mb={2}>
                                  {visibleTags &&
                                    visibleTags.map((tag, index) => (
                                      <RoundedBadge
                                        // eslint-disable-next-line react/no-array-index-key
                                        key={`${tag}-${index}`}
                                        text={tag}
                                      />
                                    ))}

                                  {remainingCount > 0 && (
                                    <Menu autoSelect={false}>
                                      <MenuButton
                                        rounded='8px'
                                        as={Button}
                                        px='12px'
                                        height={{ base: "16px", md: "24px" }}
                                        size='sm'
                                        variant='secondary'
                                        colorScheme='black'
                                        role='group'
                                        _focus={{}}
                                        _active={{}}
                                      >
                                        <Flex
                                          justifyContent='space-between'
                                          alignItems='center'
                                        >
                                          <Text
                                            fontWeight='500'
                                            fontSize='12px'
                                            lineHeight='10px'
                                            color='#006F80'
                                            _groupHover={{ color: "#fff" }}
                                          >
                                            +{remainingCount} More
                                          </Text>
                                        </Flex>
                                      </MenuButton>

                                      <MenuList minWidth='200px' zIndex={2}>
                                        {remainingTags.map((tag, idx) => (
                                          <Box
                                            // eslint-disable-next-line react/no-array-index-key
                                            key={`${tag}-${idx}`}
                                            display='block'
                                            marginBottom={{
                                              base: "3px",
                                              md: "10px",
                                            }}
                                          >
                                            <RoundedBadge
                                              marginRight='0px'
                                              text={tag}
                                            />
                                          </Box>
                                        ))}
                                      </MenuList>
                                    </Menu>
                                  )}
                                </Flex>
                              </Box>
                            </Box>
                          </Box>
                        </Td>
                        {/* <Td>
                        <Box display='flex' gap={2} alignItems='center'>
                          <Box w={2} h={2} bg='#008000' rounded='full' />
                          {data.propertyStatus}
                        </Box>
                      </Td> */}
                        {hasAccessTo?.isAdmin && <Td>{urlPropertyName}</Td>}
                        <Td>{data.addressFull?.trim()}</Td>
                        <Td>{data.pmsUnitId?.trim()}</Td>
                        {currentPmc && currentPmc.pmc_id === VACASA_PMCID && (
                          <Td fontWeight={500}>
                            {data?.pmId ? data.pmId : "-"}
                          </Td>
                        )}
                        {currentPmc &&
                          currentPmc.pmc_id === INTERHOME_PMCID && (
                            <Td fontWeight={500}>
                              {data?.pmId ? data.pmId : "-"}
                            </Td>
                          )}
                        {currentPmc &&
                          PMCIdsToDisplayUnitNumber.includes(
                            currentPmc?.pmc_id,
                          ) && (
                            <Td fontWeight={500}>
                              {data?.pmId ? data.pmId : "-"}
                            </Td>
                          )}
                        <Td>{format(new Date(data.updatedDt), "MMM dd")}</Td>
                        {hasAccessTo?.isAdmin && <Td>{data?.weight}</Td>}
                        {/* <Td>
                        <TagDetailsModal
                          details={{
                            "Property Status": data.propertyStatus,
                            "Contact Template Applied":
                              data.contactTemplateApplied ? "Yes" : "No",
                            "Discount Template Applied":
                              data.discountTemplateApplied ? "Yes" : "No",
                            "Bank Account Applied": data.accountApplied
                              ? "Yes"
                              : "No",
                          }}
                          createdAt={
                            isOlderThen30(data.createdAt)
                              ? undefined
                              : data.createdAt
                          }
                        />
                      </Td> */}
                      </CustomTableRow>
                    )
                  })}

                {isLoading &&
                  Array(pageSize)
                    .fill(1)
                    .map((x: number, ind) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <SkeletonTableRow key={`skeleton${x}${ind}`} />
                    ))}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* {!isLargerThan768 && swrdata?.content?.length !== 0 && (
        <Box>
          {checkedItems.length > 0 && (
            <Box display='flex' px={2} py={2} justifyContent='flex-end'>
              <Text mr='2'>{allChecked ? "Disselect All" : "Select All"}</Text>
              <CustomCheckBox
                isChecked={allChecked}
                onChange={(e) => {
                  if (e.target.checked) {
                    setIsSelectedAll(true)
                    setCheckedItems(
                      () => swrdata?.content.map((x) => x.pmsUnitId) || [],
                    )
                  } else {
                    setCheckedItems([])
                    setIsSelectedAll(false)
                  }
                }}
              />
            </Box>
          )}
          <Box
            display='grid'
            gridTemplateColumns='repeat(auto-fit,minmax(300px,1fr))'
            gap={5}
          >
            {!isLoading &&
              swrdata?.content.map((data) => (
                <Card
                  key={data.pmsUnitId}
                  onSelect={(e) => {
                    e.preventDefault()
                    e.stopPropagation()

                    if (checkedItems.includes(data.pmsUnitId)) {
                      // means remove
                      setCheckedItems((ids) =>
                        ids.filter((x) => x !== data.pmsUnitId),
                      )
                      setIsSelectedAll(false)
                    } else {
                      // add
                      setCheckedItems((ids) => [...ids, data.pmsUnitId])
                    }
                  }}
                  isChecked={checkedItems.includes(data.pmsUnitId)}
                  data={data}
                />
              ))}
            {isLoading &&
              Array(pageSize)
                .fill(1)
                .map((x: number, ind) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <SkeletonPropertyCard key={`skeleton${x}${ind}`} />
                ))}
          </Box>
        </Box>
      )} */}

      {!error && swrdata?.content?.length !== 0 && (
        <Box
          display='flex'
          justifyContent='flex-end'
          alignItems='center'
          gap={4}
        >
          {/* table actions paginations */}
          <PageSelector
            pageSize={pageSize}
            setPageSize={setPageSize}
            setPageStartIndex={setPageStartIndex}
          />
          <Pagination
            pageSize={pageSize}
            itemsLength={swrdata?.totalElements || totalItem}
            pageStartIndex={pageStartIndex}
            setPageStartIndex={setPageStartIndex}
          />
        </Box>
      )}
      {/* Show Alert Component */}
      <Box>{alert}</Box>
    </Box>
  )
}

export default IncompleteProperties
